// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { hasProductCustomBadges } from "@synd/App/Store/Selectors/ProductSelectors";
import Badge, { BadgePresentationProps, BadgeTypeValues } from "@synd/MobiusOverrides/Badge/Badge";
import { mergeWithCss } from "@synd/XDL/Utilities";
import React, { useState } from "react";
import { css } from "styled-components";

export type CustomBadgesStyle = Pick<BadgePresentationProps, "textBadgeCss" | "textBadgeWrapperCss"> & {
    wrapper?: InjectableCss;
};

interface OwnProps {
    product?: CartLineModel | ProductContextModel | ProductModel;
    extendedStyles?: CustomBadgesStyle;
    displayPreBookBadge?: boolean;
}

export const customBadgesStyles: CustomBadgesStyle = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 4px;
        `,
    },
    textBadgeWrapperCss: {
        css: css`
            display: flex;
            width: fit-content;
        `,
    },
};

const ProductCustomBadges = ({ product, extendedStyles, displayPreBookBadge = true }: OwnProps) => {
    const [styles] = useState(() => mergeWithCss(customBadgesStyles, extendedStyles));
    if (!product) {
        return null;
    }
    const { preBook, previouslyPurchased } = hasProductCustomBadges(product);

    if (!previouslyPurchased && !preBook) {
        return null;
    }
    return (
        <StyledWrapper {...styles.wrapper} className="product-custom-badges">
            {previouslyPurchased && (
                <Badge
                    type={BadgeTypeValues.Text}
                    value={translate("Prev. Purchased")}
                    textBadgeWrapperCss={styles.textBadgeWrapperCss}
                />
            )}
            {preBook && displayPreBookBadge && (
                <Badge
                    type={BadgeTypeValues.Text}
                    value={translate("Pre-Book")}
                    textBadgeWrapperCss={styles.textBadgeWrapperCss}
                />
            )}
        </StyledWrapper>
    );
};

export default ProductCustomBadges;
